import {
  FilterManager,
  UIManager,
  URLManager,
  FacetRenderer,
  PaginationRenderer,
  PriceRange,
  LayoutSwitcher,
  FacetToggler
} from '//99bikes.com.au/cdn/shop/t/277/assets/99-search-constructors.js?manual=20260928&v=105606246461782466231790728608';

// Main Algolia Search Implementation
class AlgoliaSearch {
  constructor() {
    this.searchManager = null;
    this.filterManager = null;
    this.uiManager = null;
    this.urlManager = null;
    this.initialized = false;
    // Add pubsub unsubscriber
    this.deliveryStoreUnsubscriber = null;
    // Cache DOM elements to avoid repeated queries
    this.cachedElements = {
      sortElements: null,
      searchInput: null,
      searchResetButton: null,
      productGrid: null,
      facetContainer: null,
    };
    // Debounced methods
    this.debouncedSearch = this.debounce(this.handleSearch.bind(this), 300);
    this.debouncedRender = this.debounce(this.performRender.bind(this), 16);
  }

  async init() {
    if (this.initialized) return;

    try {
      await this.waitForAlgolia();
      this.initializeComponents();
      this.setupEventListeners();
      this.setupPubSubListeners();
      await this.performInitialSearch();
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Algolia search:', error);
    }
  }

  waitForAlgolia() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Algolia lib not found'));
      }, 5000);

      const checkAlgolia = () => {
        if (window.algoliaShopify?.searchClient) {
          clearTimeout(timeout);
          resolve();
        } else {
          setTimeout(checkAlgolia, 100);
        }
      };
      checkAlgolia();
    });
  }

  initializeComponents() {
    this.searchManager = new SearchManager(window.algoliaShopify.searchClient);
    this.filterManager = new FilterManager();
    this.uiManager = new UIManager();
    this.urlManager = new URLManager();

    // Load initial state from URL
    this.filterManager.initializeFromURL();
    //20260615 -PO, load initial local store filter state
    this.filterManager.initialiseLocalStoreFacet(this.uiManager.currentStoreKey);

    // Populate sort options dynamically (with delay for collections)
    setTimeout(() => {
      this.populateSortOptions();
    }, 100);
  }

  cacheSortElements() {
    if (!this.cachedElements.sortElements) {
      this.cachedElements.sortElements = [
        document.getElementById('SortBy-search'),
        document.getElementById('SortBy-drawer'),
        document.getElementById('SortBy'),
        document.querySelector('select[name="sort_by"]'),
        ...document.querySelectorAll('select[data-sort-by]'),
        ...document.querySelectorAll('select[class*="sort"]'),
        ...document.querySelectorAll('select[id*="sort"]'),
        ...document.querySelectorAll('select[id*="Sort"]'),
      ]
        .filter(Boolean)
        .filter((element, index, array) => array.indexOf(element) === index);
    }
    return this.cachedElements.sortElements;
  }

  populateSortOptions() {
    const isCollection = !!window.collectionConfig;
    const sortOrders = isCollection
      ? window.algoliaShopify.collectionSortOrders
      : window.algoliaShopify.sortOrders;

    if (!sortOrders || !Array.isArray(sortOrders)) {
      console.warn('Sort orders not found in algoliaShopify config');
      return;
    }

    const sortElements = this.cacheSortElements();

    sortElements.forEach(selectElement => {
      // Clear existing options
      selectElement.innerHTML = '';

      // Create options in fragment first
      const tempFragment = document.createDocumentFragment();
      sortOrders.forEach(sortOrder => {
        const option = document.createElement('option');
        option.value = sortOrder.value;
        option.textContent = sortOrder.label;
        tempFragment.appendChild(option);
      });

      // Single DOM update per select
      selectElement.appendChild(tempFragment);
    });

    // Now update the selected value after options are populated
    this.filterManager.updateSortSelectors(this.filterManager.getSortBy());
  }

  cacheSearchElements() {
    if (!this.cachedElements.searchInput) {
      this.cachedElements.searchInput = document.querySelector(
        '#MainContent .search__input.form-control'
      );
    }
    if (!this.cachedElements.searchResetButton) {
      this.cachedElements.searchResetButton = document.querySelector('#MainContent .search__reset');
    }
    return {
      searchInput: this.cachedElements.searchInput,
      searchResetButton: this.cachedElements.searchResetButton,
    };
  }

  setupEventListeners() {
    const { searchInput, searchResetButton } = this.cacheSearchElements();

    // Search input with debounced handler
    if (searchInput) {
      searchInput.addEventListener('input', event => {
        this.debouncedSearch(event.target.value);
      });
    }

    if (searchResetButton) {
      searchResetButton.addEventListener('click', e => {
        e.preventDefault();
        searchInput.value = '';
        searchInput.focus();
        this.handleSearch('');
      });
    }

    // Sort elements - use cached elements
    const sortElements = this.cacheSortElements();
    sortElements.forEach(element => {
      element.addEventListener('change', event => this.handleSortChange(event.target.value));
    });

    // Use event delegation for filter changes
    this.setupGlobalEventDelegation();
  }

  setupGlobalEventDelegation() {
    // Single global event listener using delegation
    document.addEventListener(
      'change',
      event => {
        if (this.isFilterInput(event.target)) {
          this.handleFilterChange(event.target);
        }
      },
      { passive: true }
    );
  }

  setupPubSubListeners() {
    // Subscribe to delivery store updates
    this.deliveryStoreUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.deliveryStoreUpdated,
      this.handleDeliveryStoreUpdate.bind(this)
    );
    this.localStoreFilterUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.localStoreFilterUpdated,
      this.handleLocalStoreFilterUpdate.bind(this)
    );
  }

  async handleLocalStoreFilterUpdate(e) {
    //ignore if change made from filter manager
    if (e.source === this.filterManager) return;
    try {
      this.removeClickAndCollectFilters();
      this.filterManager.initialiseLocalStoreFacet(this.uiManager.currentStoreKey);
      
      await this.executeSearch();
    } catch(e) {
      console.error('Failed to handle local store filter update:', e);
    }
  }

  async handleDeliveryStoreUpdate(data) {
    try {
      // Remove any click and collect filters from URL and active filters
      this.removeClickAndCollectFilters();

      // Update current store in UI manager
      this.uiManager.updateCurrentStore();
      this.filterManager.initialiseLocalStoreFacet(this.uiManager.currentStoreKey);

      // Re-execute search to refresh filters with new store
      await this.executeSearch();
    } catch (error) {
      console.error('Failed to handle delivery store update:', error);
    }
  }

  removeClickAndCollectFilters() {
    // Remove from active filters
    const activeFilters = this.filterManager.getActiveFilters();
    Object.keys(activeFilters.facets).forEach(facetName => {
      if (facetName.includes('search_information.click_and_collect')) {
        delete activeFilters.facets[facetName];
      }
    });

    // Update filter manager
    this.filterManager.activeFilters.facets = activeFilters.facets;

    // Remove from URL
    this.urlManager.removeClickAndCollectFromURL();
  }

  async performInitialSearch() {
    const searchElement = document.querySelector('#MainContent .search__input.form-control');
    const hasSearchTerm = searchElement?.value;
    const hasFilters = this.filterManager.hasActiveFilters();
    const isCollection = !!window.collectionConfig;
    const initialPage = this.filterManager.getInitialPage();

    if (hasSearchTerm || hasFilters || isCollection || initialPage > 0) {
      await this.executeSearch(initialPage);
      // Reset initial page after first search to prevent it from persisting
      this.filterManager.resetInitialPage();
    }
  }

  async handleSearch(query) {
    await this.executeSearch();
  }

  handleSortChange(sortValue) {
    this.filterManager.setSortBy(sortValue);
    this.executeSearch();
  }

  handleFilterChange(input) {
    this.filterManager.updateFromInput(input);
    this.executeSearch();
  }

  async executeSearch(page = 0) {
    try {
      // Show shimmer state before starting search
      this.uiManager.showShimmerState();

      const searchParams = this.buildSearchParams(page);

      const results = await this.searchManager.search(searchParams);

      // Use debounced rendering to prevent UI blocking
      this.debouncedRender(results, page, searchParams.hitsPerPage);
      this.urlManager.updateURL(this.filterManager.getActiveFilters(), page, this.filterManager.defaultSort);
    } catch (error) {
      console.error('Search execution failed:', error);
      // Hide shimmer state on error
      this.uiManager.hideShimmerState();
    }
  }

  performRender(results, page, hitsPerPage) {
    // Use requestAnimationFrame for smooth rendering
    requestAnimationFrame(() => {
      this.uiManager.algoliaRenderResults(results, page, hitsPerPage);
    });
  }

  buildSearchParams(page = 0) {
    const searchElement = document.querySelector('#MainContent .search__input.form-control');
    const query = searchElement?.value || '';

    // Get products per page from section settings
    const sectionContainer = document.querySelector('.collection[data-products-per-page]');
    const productsPerPage = sectionContainer
      ? parseInt(sectionContainer.dataset.productsPerPage)
      : 16; // fallback to default

    return {
      query,
      page,
      filters: this.filterManager.algoliaBuildFilters(),
      facetFilters: this.filterManager.algoliaBuildFacetFilters(),
      numericFilters: this.filterManager.algoliaBuildNumericFilters(),
      sortBy: this.filterManager.getSortBy(),
      facets: this.getFacetNames(),
      hitsPerPage: productsPerPage,
    };
  }

  getFacetNames() {
    const facetsConfig = window.collectionConfig
      ? window.algoliaShopify.collectionFacets
      : window.algoliaShopify.facets;

    return facetsConfig
      ? facetsConfig.filter(facet => facet.enabled).map(facet => facet.escapedName)
      : ['vendor', 'product_type', 'tags', 'price_range'];
  }

  isFilterInput(element) {
    // Exclude price inputs since they have their own dedicated handlers
    return element.matches('input[name^="filter.v."]:not([name*="price"]), select[name*="sort"]');
  }

  debounce(func, wait = 150) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  updateSortSelectors(sortBy) {
    // Use cached sort elements
    const sortElements = this.cacheSortElements();

    // Batch DOM updates
    requestAnimationFrame(() => {
      sortElements.forEach(element => {
        if (element) element.value = sortBy;
      });
    });
  }

  // Add cleanup method
  destroy() {
    this.deliveryStoreUnsubscriber?.();
    this.localStoreFilterUnsubscriber?.();
  }
}

// Search Manager - Handles Algolia API interactions
class SearchManager {
  constructor(searchClient) {
    this.searchClient = searchClient;
    this.currentIndex = 'shopify_products';
  }

  async search(params) {
    try {
      //Check if searchTerm is valid first
      if (params.query) {
        if ((window.theme?.searchTermCharLimit && params.query.length > window.theme.searchTermCharLimit) 
          || (window.theme?.searchTermRegexCheck instanceof RegExp && !window.theme.searchTermRegexCheck.test(params.query))) {
          return {
            groupedHits: [],
            nbHits: 0
          };
        }
      }

      // Step 1: Get initial hits to extract product IDs
      const initialResults = await this.getInitialHits(params);

      if (initialResults.hits.length === 0) {
        return this.processResults(initialResults);
      }

      // Step 2: Extract unique product IDs
      const productIds = this.extractProductIds(initialResults.hits);

      // Step 3: Fetch all variants for these products
      const allVariantsResults = await this.getAllVariantsForProducts(productIds, params);

      // Step 4: Get price context to show accurate min/max for current filter set
      let priceContextStats = null;
      // Always get price context when we have other filters active, or when price filter is active
      if (this.hasPriceFilter(params) || this.hasOtherFilters(params)) {
        priceContextStats = await this.getPriceContext(params);
      }

      // Step 5: Combine results, keeping the original hits order and supplementing with additional variants
      const combinedResults = {
        ...initialResults,
        // Keep original hits (correct order) and supplement with additional variants
        hits: initialResults.hits,
        additionalVariants: allVariantsResults.hits,
        // Always use facets from initial search - this gives correct counts for filtering
        facets: initialResults.facets,
        facets_stats: initialResults.facets_stats,
        // Add price context for true max price when price filter is active
        price_context_stats: priceContextStats,
      };

      return this.processResults(combinedResults);
    } catch (error) {
      console.error('Search failed:', error);
      throw error;
    }
  }

  async getInitialHits(params) {
    const indexName = this.getSearchIndex(params.sortBy);
    const searchParams = this.buildAlgoliaParams(params);
    // Use distinct to get one hit per product for initial search
    searchParams.distinct = 1;

    const multipleSearchParams = [
      {
        query: params.query,
        indexName: indexName,
        params: searchParams,
      },
    ];

    // Add additional facet queries for each facet filter
    if (Array.isArray(searchParams.facetFilters) && searchParams.facetFilters.length > 0) {
      searchParams.facetFilters.forEach((filter, idx) => {
        // Helper to extract facet name
        const getFacetName = f => {
          const facetGroup = Array.isArray(f) ? f[0] : f;
          // Split the filter by the first colon to get the facet name e.g vendor:Merida -> vendor
          return facetGroup.split(':')[0];
        };

        // Remove Duplicate Filters
        const filters = searchParams.facetFilters.filter((_, i) => i !== idx);
        const facet = getFacetName(filter);

        multipleSearchParams.push({
          query: params.query,
          indexName: 'shopify_products',
          params: { ...searchParams, facets: facet, facetFilters: filters },
        });
      });
    }

    const multipleResults = await this.searchClient.multipleQueries(multipleSearchParams);

    // The first Result contains the main search data as well as all facets.
    const searchData = multipleResults.results[0];

    if (multipleResults.results.length > 1) {
      const additionalFacets = multipleResults.results.slice(1).reduce((acc, result) => {
        return Object.assign(acc, result.facets);
      }, {});

      searchData.facets = Object.assign({}, searchData.facets, additionalFacets);
    }

    return searchData;
  }

  extractProductIds(hits) {
    const uniqueIds = new Set();
    hits.forEach(hit => {
      if (hit.id) {
        uniqueIds.add(hit.id);
      }
    });
    return Array.from(uniqueIds);
  }

  async getAllVariantsForProducts(productIds, params) {
    if (productIds.length === 0) {
      return { hits: [], facets: {}, facets_stats: {} };
    }

    // Build filter string for all product IDs
    const productIdFilters = productIds.map(id => `_tags:"id:${id}"`).join(' OR ');

    // Use the base index (no sorting) for variant fetching to ensure we get all variants
    const index = this.searchClient.initIndex('shopify_products');

    // For the variant fetch, only filter by product IDs to get ALL variants for selected products
    const searchParams = {
      filters: productIdFilters,
      facets: [], // Don't calculate facets for this search - we use the initial search facets
      hitsPerPage: 1000, // High limit to ensure we get all variants
      distinct: false,
    };

    const results = await index.search('', searchParams);

    // Sort the results to match the original product order
    const sortedHits = this.sortVariantsByOriginalOrder(results.hits, productIds);

    return {
      ...results,
      hits: sortedHits,
    };
  }

  sortVariantsByOriginalOrder(variants, productIds) {
    // Create a map of product ID to its index in the original order
    const orderMap = new Map();
    productIds.forEach((id, index) => {
      orderMap.set(id, index);
    });

    // Group variants by product ID
    const variantsByProduct = new Map();
    variants.forEach(variant => {
      const productId = variant.id;
      if (!variantsByProduct.has(productId)) {
        variantsByProduct.set(productId, []);
      }
      variantsByProduct.get(productId).push(variant);
    });

    // Sort variants within each product by position
    variantsByProduct.forEach(variants => {
      variants.sort((a, b) => a.position - b.position);
    });

    // Build final sorted array following original product order
    const sortedVariants = [];
    productIds.forEach(productId => {
      const productVariants = variantsByProduct.get(productId);
      if (productVariants) {
        sortedVariants.push(...productVariants);
      }
    });

    return sortedVariants;
  }

  hasPriceFilter(params) {
    return params.numericFilters && params.numericFilters.length > 0;
  }

  hasOtherFilters(params) {
    // Check if we have any facet filters (brand, category, etc.) or search query
    return (
      (params.facetFilters && params.facetFilters.length > 0) ||
      (params.query && params.query.trim().length > 0) ||
      window.collectionConfig?.handle // Collection page
    );
  }

  async getPriceContext(params) {
    try {
      const indexName = this.getSearchIndex(params.sortBy);

      // Build search params without numeric filters (price filters)
      const contextParams = this.buildAlgoliaParams({
        ...params,
        numericFilters: [], // Remove price filters
      });

      // Only request price facet stats, no hits needed
      contextParams.hitsPerPage = 0;
      contextParams.facets = ['price'];

      const contextResults = await this.searchClient
        .initIndex(indexName)
        .search(params.query || '', contextParams);

      return contextResults.facets_stats?.price || null;
    } catch (error) {
      console.error('Failed to get price context:', error);
      return null;
    }
  }

  getSearchIndex(sortBy) {
    // If sortBy is already an index name (from the new config), use it directly
    if (sortBy && sortBy.startsWith('shopify_products')) {
      return sortBy;
    }

    // Fallback for legacy sort values
    let indexName = 'shopify_products';

    switch (sortBy) {
      case 'price-ascending':
        indexName = 'shopify_products_price_asc';
        break;
      case 'price-descending':
        indexName = 'shopify_products_price_desc';
        break;
      case 'relevance':
      default:
        indexName = 'shopify_products';
        break;
    }

    return indexName;
  }

  buildAlgoliaParams(params) {
    const algoliaParams = {
      filters: 'inventory_available:true',
      facets: params.facets,
      page: params.page,
      hitsPerPage: params.hitsPerPage,
      maxValuesPerFacet: window.algoliaShopify?.config?.default_facet_param_limit || 10,
      distinct: params.distinct || 10,
      clickAnalytics: true, //99B 20250929 -PO, get queryID from initial search for analytics
    };

    // Add collection filter
    if (window.collectionConfig?.handle) {
      algoliaParams.filters += ` AND collections:${window.collectionConfig.handle}`;
    }

    // Add additional filters
    if (params.filters) {
      algoliaParams.filters += ` AND ${params.filters}`;
    }

    if (params.facetFilters?.length > 0) {
      algoliaParams.facetFilters = params.facetFilters;
    }

    if (params.numericFilters?.length > 0) {
      algoliaParams.numericFilters = params.numericFilters;
    }

    return algoliaParams;
  }

  processResults(results) {
    // Group variants by product ID from the additional variants search
    const additionalVariantsByProduct = new Map();

    if (results.additionalVariants) {
      results.additionalVariants.forEach(variant => {
        const productId = variant.id;
        if (!additionalVariantsByProduct.has(productId)) {
          additionalVariantsByProduct.set(productId, []);
        }
        additionalVariantsByProduct.get(productId).push(variant);
      });
    }

    // Process the original hits (which are in correct order) and supplement with additional variants
    const groupedHits = results.hits.map(hit => {
      const productId = hit.id;
      const additionalVariants = additionalVariantsByProduct.get(productId) || [];

      // Combine the original hit with additional variants, removing duplicates
      const allVariants = [hit];
      additionalVariants.forEach(variant => {
        // Only add if we don't already have this variant
        if (!allVariants.some(existing => existing.objectID === variant.objectID)) {
          allVariants.push(variant);
        }
      });

      // Sort variants by position
      allVariants.sort((a, b) => a.position - b.position);

      const { metafields, ...productData } = hit;
      return {
        ...productData,
        variants: allVariants,
      };
    });

    return {
      ...results,
      groupedHits,
    };
  }
}

// Register custom elements
customElements.define('price-range', PriceRange);
customElements.define('layout-switcher', LayoutSwitcher);
customElements.define('facet-toggler', FacetToggler, { extends: 'button' });

// Initialize the application
document.addEventListener('DOMContentLoaded', async () => {
  window.searchProvider = new AlgoliaSearch();
  await window.searchProvider.init();
});
